import { query, findRecord } from '@warp-drive/utilities/json-api';
import { saveRecord, deleteExistingRecord, reloadRecord } from 'tio-data/requests/save-record';
import { abandonTasApprovableRequest } from 'tio-data/requests/tas-approvable';
import { mapAssistancesToTransactions } from 'tio-employee/utils/course-cash/transaction-adapter';
import type { Store as StoreService } from '@warp-drive/core';
import type {
  CourseCashReceipt,
  CourseCashTransaction,
} from 'tio-common/types/course-cash-transaction';
import type DigitalAssetsService from 'tio-common/services/digital-assets';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';
import type { AttachmentExtension as AttachmentModel } from 'tio-data/data/resources/attachment.ext';
import type { TasApprovableExtension as ApprovableModel } from 'tio-data/data/resources/tas-approvable.ext';

/**
 * CourseCash receipt upload/removal logic shared by the legacy show page
 * (`authenticated/tas/applications/show.gts`) and the reimagined "manage"
 * screen (`components/tas/applications/manage/manage-page.gts`), so both read
 * and mutate the same live receipt state instead of drifting copies.
 */

/**
 * Query the /tas-approvables endpoint filtered to the given assistances and
 * group results by assistance ID.
 *
 * Note: cannot include approvable.attachments — JSONAPI::Resources can't
 * traverse through a polymorphic relationship. Attachments are expected to
 * already be in the store from the route's
 * tas-assistances.tas-assets.attachments include.
 */
export async function queryApprovablesByAssistance(
  store: StoreService,
  assistances: { id: string | null }[]
): Promise<Map<string, ApprovableModel[]>> {
  const map = new Map<string, ApprovableModel[]>();

  // Short-circuit when there are no captured Course Cash assistances
  const assistanceIds = new Set(assistances.filter((a) => a.id).map((a) => a.id!));
  if (assistanceIds.size === 0) return map;

  try {
    const { content } = (await store.request(
      query<ApprovableModel>('tas-approvable', {
        'filter[approval]': 'ApprovalType.COURSE_CASH_USAGE_REVIEW',
        'filter[context_type]': 'TasAssistance',
        'filter[context_id]': [...assistanceIds].join(','),
        include: 'approvable,in-context',
      })
    )) as { content: { data: ApprovableModel[] } };

    for (const approvable of content.data) {
      // in-context relationship links approvable to its TasAssistance
      const contextId = approvable.inContext?.id;
      if (!contextId || !assistanceIds.has(contextId)) continue;

      const existing = map.get(contextId) || [];
      existing.push(approvable);
      map.set(contextId, existing);
    }
  } catch (error) {
    console.error('[CourseCash] Failed to query approvables endpoint:', error);
    // On failure, the transaction adapter falls back to sideloaded relationship data
  }

  return map;
}

/**
 * Load Course Cash transactions from the approvables endpoint + the
 * application's sideloaded assistance data.
 *
 * `includeAuthorized` surfaces AUTHORIZED (pending-capture) swipes as
 * informational transactions — passed by the manage screen only; the legacy
 * show page calls this without options and keeps captured-only behavior.
 */
export async function fetchCourseCashTransactions(
  store: StoreService,
  application: TasApplicationModel,
  options: { includeAuthorized?: boolean } = {}
): Promise<CourseCashTransaction[]> {
  const assistances = application.tasAssistances ?? [];
  // Receipt approvables only ever exist for captured course-cash usage, so the
  // endpoint query is scoped to those ids — authorized/reserved siblings would
  // just pad the filter with ids that can never match.
  const capturedAssistances = assistances.filter((a) => a.isCourseCash && a.isCaptured);
  const approvablesByAssistance = await queryApprovablesByAssistance(store, capturedAssistances);
  return mapAssistancesToTransactions(assistances, approvablesByAssistance, options);
}

/**
 * Standalone refresh to pull the latest assistances/attachments (NOT after a
 * builder mutation on this record), then rebuild the transaction list.
 *
 * Do NOT include 'tas-assistances.approvables' in the reload — the backend
 * returns approvables with the base STI type "approvables" which has no
 * Ember Data model; approvables are queried directly via
 * queryApprovablesByAssistance() instead, which returns the correct
 * "tas-approvables" type.
 */
export async function refreshCourseCashTransactions(
  store: StoreService,
  application: TasApplicationModel,
  options: { includeAuthorized?: boolean } = {}
): Promise<CourseCashTransaction[]> {
  const assistanceIncludes = [
    'tas-assistances',
    'tas-assistances.attachments',
    'tas-assistances.tas-assets',
    'tas-assistances.tas-assets.attachments',
  ];
  await reloadRecord(store, application, { include: assistanceIncludes.join(',') });

  return fetchCourseCashTransactions(store, application, options);
}

/**
 * Upload receipt file(s) for a Course Cash transaction: creates a TasAsset per
 * file with CARD_USAGE_PROOF classification, links it to the transaction's
 * assistance(s) and the application, then abandons any pending approvables
 * the new upload supersedes.
 */
export async function uploadCourseCashReceipt(
  store: StoreService,
  digitalAssets: DigitalAssetsService,
  application: TasApplicationModel,
  files: DigitalAssetDetailSignature[],
  transaction: CourseCashTransaction
): Promise<void> {
  if (!transaction.assistanceId) {
    throw new Error('No assistance ID found for transaction');
  }

  // Find the assistance record(s). A merged split transaction represents two captured
  // assistances (charge leg + fee leg); the receipt must attach to BOTH so the fee
  // leg's server-side proof-of-payment demand (and its overdue emails) is satisfied,
  // not just the charge leg's.
  const legIds = transaction.splitLegs?.length
    ? transaction.splitLegs.map((leg) => leg.assistanceId)
    : [transaction.assistanceId];
  const assistances = legIds
    .map((id) => application.tasAssistances?.find((a) => a.id === id))
    .filter((a) => a !== undefined);

  // Fail fast unless EVERY leg resolves — attaching to only one leg would silently
  // leave the other leg's proof-of-payment demand (and overdue emails) outstanding.
  if (assistances.length !== legIds.length) {
    throw new Error('Could not find assistance record(s) for all transaction legs');
  }

  // Upload each file and create asset + attachment with CARD_USAGE_PROOF classification
  for (const fileDetails of files) {
    const asset = digitalAssets.createAssetFromS3Details('tasAsset', fileDetails) as TasAssetModel;
    asset.classification = 'CARD_USAGE_PROOF';

    // Link asset to assistance(s) - update both sides of many-to-many relationship
    asset.tasAssistances = asset.tasAssistances
      ? [...asset.tasAssistances, ...assistances]
      : [...assistances];

    // Link asset to application - update both sides of many-to-many relationship
    asset.tasApplications = asset.tasApplications
      ? [...asset.tasApplications, application]
      : [application];
    application.tasAssets.push(asset);

    // Save the asset to create the join record; avoid saving the assistance
    // because tas-assistances has read-only attributes on update.
    await saveRecord(store, asset);
  }

  // Abandon any existing pending approvables — uploading a new receipt supersedes them.
  // The new asset will get its own approvable via TasAssetClassificationJob.
  const pendingReceipts = transaction.receipts.filter(
    (r) => r.status === 'pending_verification' && r.approvableId
  );
  for (const receipt of pendingReceipts) {
    try {
      await store.request(abandonTasApprovableRequest(receipt.approvableId!));
    } catch (error) {
      console.error('[CourseCash] Failed to abandon pending approvable on re-upload:', error);
    }
  }
}

/** Removes an uploaded receipt and abandons its associated review, if any. */
export async function removeCourseCashReceipt(
  store: StoreService,
  receipt: CourseCashReceipt
): Promise<void> {
  if (!receipt.attachmentId) {
    throw new Error('No attachment ID found for receipt');
  }

  let attachment = store.peekRecord('attachment', receipt.attachmentId) as AttachmentModel | null;

  if (!attachment) {
    const { content } = (await store.request(
      findRecord<AttachmentModel>('attachment', receipt.attachmentId)
    )) as { content: { data: AttachmentModel } };
    attachment = content.data;
  }

  if (!attachment) {
    throw new Error('Could not find attachment for receipt');
  }

  await deleteExistingRecord(store, attachment);

  // Abandon the associated TasApprovable when a receipt is removed.
  // Intentionally done AFTER destroyRecord: if abandon fails the orphaned
  // approvable is harmless (determineReceiptStatus handles it), but the
  // reverse — abandoning then failing to delete — leaves a verified-looking
  // file attached with no active review, which is worse.
  if (receipt.approvableId) {
    try {
      await store.request(abandonTasApprovableRequest(receipt.approvableId));
    } catch (error) {
      console.error('[CourseCash] Failed to abandon approvable:', error);
      // Non-blocking: the caller's refresh below reloads state from the server
    }
  }
}
